import { Palette, Zap, Layers, Maximize2, Battery, Gem } from "lucide-react";
import { IMG } from "@/data/catalog";
import { mediaUrl as M } from "@/data/pdpMedia";
import { Reveal, SectionHeading } from "@/components/common/ui";
import { Blush } from "@/components/common/Decor";

const FEATURES = [
  { icon: Gem, title: "Designed To Love", tag: "Pillow-soft, beautifully made" },
  { icon: Palette, title: "Targets Every Concern", tag: "4 wavelengths · 6 modes" },
  { icon: Zap, title: "Pro Results At Home", tag: "53 mW/cm² · 114 LEDs" },
  { icon: Layers, title: "Pillow-Soft Comfort", tag: "Soft silicone, lightweight fit" },
  { icon: Battery, title: "Glow Anywhere", tag: "Rechargeable & cord-free" },
  { icon: Maximize2, title: "Fits Every Face", tag: "Secure, hands-free fit" },
];

const Callout = ({ icon: Icon, title, tag, side, delay }) => {
  const iconEl = (
    <span className="flex h-11 w-11 flex-none items-center justify-center rounded-full bg-[#FBC4A8] text-[#2C2B2C] shadow-[0_10px_22px_-12px_rgba(120,80,55,0.7)] transition-transform group-hover:scale-110">
      <Icon className="h-5 w-5" strokeWidth={1.75} />
    </span>
  );
  const textEl = (
    <div className="min-w-0">
      <p className="whitespace-nowrap font-display text-base leading-tight text-[#FFFCFA] md:text-lg">{title}</p>
      <p className="mt-0.5 text-xs font-light leading-snug text-[#FFFFFF]/55">{tag}</p>
    </div>
  );
  const lineEl = <span className="hidden h-px w-8 flex-none bg-[#FBC4A8]/70 lg:block xl:w-12" />;
  const dotEl = <span className="hidden h-1.5 w-1.5 flex-none rounded-full bg-[#FBC4A8] lg:block" />;
  return (
    <Reveal delay={delay} className={`group flex items-center gap-3 ${side === "right" ? "text-left" : "text-left lg:text-right"}`}>
      {side === "left" ? (
        <>{iconEl}{textEl}{lineEl}{dotEl}</>
      ) : (
        <>{dotEl}{lineEl}{iconEl}{textEl}</>
      )}
    </Reveal>
  );
};

export const LightModes = () => (
  <section className="relative overflow-hidden bg-[#232222] pt-24 pb-10 md:pt-32 md:pb-12" data-testid="light-modes">
    <Blush className="left-1/2 top-[46%] -translate-x-1/2 -translate-y-1/2" color="#FBC4A8" size="30rem" style={{ opacity: 0.16 }} />
    <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12">
      <SectionHeading align="center" overline="Engineered For Perfection"
        title={<span className="text-[#FFFCFA]">Every detail, <span className="text-[#FBC4A8]">considered.</span></span>}
        sub={<span className="text-[#FFFFFF]/65">Not another gadget. A device engineered around how you want to feel, glowing, confident and effortlessly looked-after.</span>}
        className="mb-12 [&_h2]:text-[#FFFCFA]" />

      <div className="mx-auto grid max-w-[1040px] items-center gap-y-10 lg:grid-cols-[1fr_auto_1fr] lg:gap-x-4 xl:gap-x-8">
        {/* Left callouts — desktop only */}
        <div className="hidden lg:order-1 lg:flex lg:flex-col lg:gap-12 lg:items-end">
          {FEATURES.slice(0, 3).map((f, i) => <Callout key={f.title} {...f} side="left" delay={i * 0.08} />)}
        </div>

        {/* Center mask */}
        <div className="relative order-1 flex justify-center lg:order-2">
          <Reveal className="relative z-10 w-full max-w-[400px] lg:-mb-12">
            <img src={M("pdp_every_detail")} data-media-slot="pdp_every_detail" data-media-type="image" alt="Woman wearing the Leopard Luxe™ LED light therapy mask" className="w-full object-contain" style={{ WebkitMaskImage: "linear-gradient(to bottom, black 82%, transparent 99%)", maskImage: "linear-gradient(to bottom, black 82%, transparent 99%)" }} />
          </Reveal>
        </div>

        {/* Right callouts — desktop only */}
        <div className="hidden lg:order-3 lg:flex lg:flex-col lg:gap-12 lg:items-start">
          {FEATURES.slice(3).map((f, i) => <Callout key={f.title} {...f} side="right" delay={i * 0.08} />)}
        </div>
      </div>

      {/* Mobile: horizontal sliding feature cards */}
      <div className="mt-4 flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2 lg:hidden [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {FEATURES.map(({ icon: Icon, title, tag }) => (
          <div key={title} className="flex w-[64%] shrink-0 snap-start items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] p-3.5 sm:w-[44%]">
            <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-[#FBC4A8] text-[#2C2B2C]"><Icon className="h-5 w-5" strokeWidth={1.75} /></span>
            <div className="min-w-0">
              <p className="font-display text-sm leading-tight text-[#FFFCFA]">{title}</p>
              <p className="mt-0.5 text-[0.68rem] font-light leading-snug text-white/55">{tag}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);
